import { useState, useEffect, useCallback, useRef } from "react";
import { PlayerDetails, User } from "../../types";
import { FontOptions } from "../../types";
import { Avatar } from "../../components/menu/name/sections/AvatarSelection";
import {
  PlayerProfileSettings,
  PlayerSettingsState,
  UsePlayerSettingsReturn,
} from "../../types/PlayerSettingsTypes";
import { getValidSpriteId, DEFAULT_AVATAR_ID } from "../../utils/avatars";

const names: Avatar[] = [
  { id: 0, name: "ghost" },
  { id: 24, name: "cryghost", premium: true },
  { id: 1, name: "demon" },
  { id: 2, name: "raccoon" },
  { id: 3, name: "trash" },
  { id: 4, name: "dog" },
  { id: 5, name: "spike" },
  { id: 6, name: "armpit" },
  { id: 7, name: "cat" },
  { id: 8, name: "rabbit" },
  { id: 9, name: "potato" },
  { id: 25, name: "sparkletato", premium: true },
  { id: 10, name: "buffpire" },
  { id: 26, name: "buffstare", premium: true },
  { id: 11, name: "burger" },
  { id: 12, name: "dino" },
  { id: 13, name: "kitsune" },
  { id: 27, name: "glarefox", premium: true },
  { id: 14, name: "protec" },
  { id: 15, name: "ratty" },
  { id: 16, name: "reapr" },
  { id: 28, name: "reaprlash", premium: true },
  { id: 17, name: "skellybts" },
  { id: 29, name: "pumpedsklly", premium: true },
  { id: 18, name: "teagor" },
  { id: 19, name: "wormman" },
  { id: 30, name: "shinyworm", premium: true },
  { id: 20, name: "hedgee" },
  { id: 21, name: "scrambles" },
  { id: 22, name: "poulet" },
  { id: 23, name: "plat", premium: true, konami: true },
];

function getAvatars(premium?: boolean, konami?: boolean) {
  return names
    .filter((a) => premium || !a.premium)
    .filter((a) => konami || !a.konami || premium);
}

export const usePlayerSettings = (
  initialPlayer?: PlayerDetails,
  user?: User,
  konami?: boolean
): UsePlayerSettingsReturn => {
  // Track if we've loaded initial settings to prevent re-loading on subsequent renders
  const hasLoadedInitialSettings = useRef(false);

  // Initialize state from props or localStorage
  const [settings, setSettings] = useState<PlayerSettingsState>(() => {
    const filteredAvatars = getAvatars(user?.premium, konami);

    return {
      profile: {
        name: initialPlayer ? initialPlayer.name : "",
        mature: initialPlayer ? initialPlayer.meta.mature : false,
        accessibility: initialPlayer
          ? initialPlayer.meta.accessibility2
            ? FontOptions.COMIC_SANS
            : FontOptions.DEFAULT
          : FontOptions.DEFAULT,
        needsQuickstart: true,
      },
      avatar: {
        spriteId:
          initialPlayer && initialPlayer.name
            ? initialPlayer.meta.sprite_id
            : Math.floor(Math.random() * filteredAvatars.length),
        filteredAvatars,
      },
    };
  });

  // Load from localStorage or initialPlayer (localStorage takes priority)
  // Only run this once on mount to avoid overwriting user changes during editing
  useEffect(() => {
    // Skip if we've already loaded initial settings
    if (hasLoadedInitialSettings.current) {
      return;
    }
    hasLoadedInitialSettings.current = true;
    // Always check localStorage first
    const savedName = localStorage.getItem("playerName");
    const savedSprite = localStorage.getItem("playerSprite");
    const savedMature = localStorage.getItem("playerMature");
    const savedAccessibility = localStorage.getItem("playerAccessibility");
    const savedNeedsQuickstart = localStorage.getItem("playerNeedsQuickstart");

    const hasLocalStorageData = savedName || savedSprite || savedMature || savedAccessibility || savedNeedsQuickstart;

    if (hasLocalStorageData) {
      // Validate saved sprite against current user's premium status
      const savedSpriteId = savedSprite ? parseInt(savedSprite) : DEFAULT_AVATAR_ID;
      const userPremium = user?.premium ?? false;
      const validSpriteId = getValidSpriteId(savedSpriteId, userPremium, konami);

      // Use localStorage data (user's current session preferences)
      // needsQuickstart defaults to true if not set
      setSettings((prev) => ({
        ...prev,
        profile: {
          ...prev.profile,
          ...(savedName && { name: savedName }),
          ...(savedMature && { mature: savedMature === "true" }),
          ...(savedAccessibility && { accessibility: parseInt(savedAccessibility) }),
          needsQuickstart: savedNeedsQuickstart !== null ? savedNeedsQuickstart === "true" : true,
        },
        avatar: {
          ...prev.avatar,
          spriteId: validSpriteId,
        },
      }));
    } else if (initialPlayer) {
      // Only use initialPlayer if no localStorage data exists
      setSettings((prev) => ({
        ...prev,
        profile: {
          ...prev.profile,
          name: initialPlayer.name,
          mature: initialPlayer.meta.mature,
          accessibility: initialPlayer.meta.accessibility2
            ? FontOptions.COMIC_SANS
            : FontOptions.DEFAULT,
          needsQuickstart: true,
        },
        avatar: {
          ...prev.avatar,
          spriteId: initialPlayer.meta.sprite_id,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - auth loading prevents premature execution

  // Update filtered avatars when user premium status or konami changes
  useEffect(() => {
    const filteredAvatars = getAvatars(user?.premium, konami);
    setSettings((prev) => {
      const userPremium = user?.premium ?? false;
      const validSpriteId = getValidSpriteId(prev.avatar.spriteId, userPremium, konami);

      return {
        ...prev,
        avatar: {
          ...prev.avatar,
          spriteId: validSpriteId,
          filteredAvatars,
        },
      };
    });
  }, [konami, user?.premium]);

  // Handle konami special case
  useEffect(() => {
    if (konami) {
      localStorage.setItem("plat", "true");
      setSettings((prev) => ({
        ...prev,
        avatar: {
          ...prev.avatar,
          spriteId: 23,
        },
      }));
    }
  }, [konami]);

  const updateProfile = useCallback(
    (updates: Partial<PlayerProfileSettings>) => {
      setSettings((prev) => ({
        ...prev,
        profile: { ...prev.profile, ...updates },
      }));
    },
    []
  );

  const updateAvatar = useCallback((spriteId: number) => {
    setSettings((prev) => ({
      ...prev,
      avatar: { ...prev.avatar, spriteId },
    }));
  }, []);

  const resetToDefaults = useCallback(() => {
    const filteredAvatars = getAvatars(user?.premium, konami);
    setSettings({
      profile: {
        name: "",
        mature: false,
        accessibility: FontOptions.DEFAULT,
        needsQuickstart: true,
      },
      avatar: {
        spriteId: Math.floor(Math.random() * filteredAvatars.length),
        filteredAvatars,
      },
    });
  }, [user?.premium, konami]);

  return {
    settings,
    updateProfile,
    updateAvatar,
    resetToDefaults,
  };
};
